/**
 * Trap Manager
 * Handles passive trapping mechanic: Set -> Soak -> Pull -> Cooldown
 * Creatures caught go into the same inventory as fish.
 */

import {
  TrapStatus,
  TrapPullResult,
  FishInstance,
  FishDefinition,
  EquipmentDefinition,
  EquipmentSlot,
  GameEvent,
  QualityMultipliers,
} from '@/types';
import {
  getZoneDefinition,
  getFishDefinition,
  getEquipmentDefinition,
  RarityWeights,
  BALANCE_CONFIG,
} from '@/data';
import { useGameStore } from '@/state';
import { eventBus } from '@/services/EventBus';
import { saveManager } from '@/systems/persistence/SaveManager';
import {
  weightedRandom,
  randomInt,
  rollFishLength,
  rollFishWeight,
  rollFishHealth,
  rollIsWild,
  applyWildLengthBonus,
  applyWildWeightBonus,
  calculateFishQuality,
} from '@/utils';

export class TrapManager {
  private tickInterval: number | null = null;

  // === CORE ACTIONS ===

  /**
   * Set a trap in the water.
   * Validates ownership, status, inventory capacity.
   * Consumes trap bait if selected.
   * @returns true if trap was set successfully
   */
  setTrap(): boolean {
    const state = useGameStore.getState();
    const trapState = state.playerState.trapState;

    // Validate: status must be EMPTY
    if (trapState.status !== TrapStatus.EMPTY) {
      console.warn('🪤 Cannot set trap - trap is not empty (status:', trapState.status, ')');
      return false;
    }

    // Validate: player owns a trap (check equippedGear[TRAP])
    const equippedTrap = this.getEquippedTrap();
    if (!equippedTrap) {
      console.warn('🪤 Cannot set trap - no trap equipped');
      return false;
    }

    // Validate: the equipped item is actually a trap (not trap bait)
    if (equippedTrap.isTrapBait) {
      console.warn('🪤 Cannot set trap - equipped item is trap bait, not a trap');
      return false;
    }

    // Validate: inventory not full (need room for creatures)
    const inventory = state.playerState.inventory;
    if (inventory.currentCount >= inventory.capacity) {
      console.warn('🪤 Cannot set trap - inventory is full');
      eventBus.emit(GameEvent.INVENTORY_FULL, {});
      return false;
    }

    // Get soak duration from trap equipment definition
    const soakDurationMs = equippedTrap.trapSoakMs || 120000; // Default 2 min
    const now = Date.now();

    // If selectedTrapBait is set, consume 1 unit
    let baitConsumed = false;
    const selectedBait = trapState.selectedTrapBait;

    if (selectedBait) {
      const ownedBait = state.playerState.ownedEquipment[selectedBait];
      if (ownedBait && ownedBait.quantity > 0) {
        // Consume 1 bait via immer draft
        useGameStore.setState((draft) => {
          const baitData = draft.playerState.ownedEquipment[selectedBait];
          if (baitData) {
            baitData.quantity -= 1;
          }
        });

        baitConsumed = true;
        const remainingQty = useGameStore.getState().playerState.ownedEquipment[selectedBait]?.quantity ?? 0;

        // Emit bait used event
        eventBus.emit(GameEvent.TRAP_BAIT_USED, {
          baitId: selectedBait,
          remainingQuantity: remainingQty,
        });

        console.log(`🪤 Trap bait consumed: ${selectedBait} (${remainingQty} remaining)`);

        // If bait ran out, clear the selection
        if (remainingQty <= 0) {
          useGameStore.setState((draft) => {
            draft.playerState.trapState.selectedTrapBait = null;
          });
          console.log('🪤 Trap bait depleted, selection cleared');
        }
      } else {
        // Bait no longer available, clear the selection
        useGameStore.setState((draft) => {
          draft.playerState.trapState.selectedTrapBait = null;
        });
        console.warn('🪤 Selected trap bait not available, cleared selection');
      }
    }

    // Update trapState: status=SOAKING, soakStartedAt, soakDurationMs, readyAt
    useGameStore.setState((draft) => {
      const ts = draft.playerState.trapState;
      ts.status = TrapStatus.SOAKING;
      ts.soakStartedAt = now;
      ts.soakDurationMs = soakDurationMs;
      ts.readyAt = now + soakDurationMs;
      ts.cooldownEndsAt = null;
      ts.totalTrapsSet += 1;
    });

    // Emit TRAP_SET event
    eventBus.emit(GameEvent.TRAP_SET, {
      trapId: equippedTrap.id,
      baitId: baitConsumed ? selectedBait : null,
      soakDurationMs,
    });

    console.log(`🪤 Trap set! Soaking for ${Math.round(soakDurationMs / 1000)}s`);

    // Save to localStorage
    saveManager.save();

    return true;
  }

  /**
   * Pull a ready trap to collect creatures.
   * Generates creatures, adds to inventory, updates stats.
   * @returns TrapPullResult or null if trap is not ready
   */
  pullTrap(): TrapPullResult | null {
    const state = useGameStore.getState();
    const trapState = state.playerState.trapState;

    // Validate: status must be READY
    if (trapState.status !== TrapStatus.READY) {
      console.warn('🪤 Cannot pull trap - trap is not ready (status:', trapState.status, ')');
      return null;
    }

    // Generate creatures
    const creatures = this.generateCreatures();

    // Add each creature to inventory and track discovery
    let addedCount = 0;
    const addedCreatures: { fish: FishInstance; definition: FishDefinition }[] = [];

    for (const creature of creatures) {
      const added = useGameStore.getState().addFishToInventory(creature.fish);
      if (added) {
        useGameStore.getState().trackFishDiscovery(creature.fish);
        addedCreatures.push(creature);
        addedCount++;
      } else {
        // Inventory full, stop adding
        console.warn('🪤 Inventory full, could not add all creatures');
        break;
      }
    }

    // Calculate total value of added creatures
    const totalValue = addedCreatures.reduce((sum, c) => {
      const qualityMult = QualityMultipliers[c.fish.quality] ?? 1.0;
      return sum + Math.round(c.definition.baseValue * qualityMult);
    }, 0);

    const soakDurationMs = trapState.soakDurationMs;
    const baitUsed = trapState.selectedTrapBait;

    // Build result
    const result: TrapPullResult = {
      creatures: addedCreatures,
      totalValue,
      soakDurationMs,
      baitUsed,
      timestamp: Date.now(),
    };

    // Update trapState stats and transition to COOLDOWN
    useGameStore.setState((draft) => {
      const ts = draft.playerState.trapState;
      ts.status = TrapStatus.COOLDOWN;
      ts.cooldownEndsAt = Date.now() + BALANCE_CONFIG.TRAP_COOLDOWN_MS;
      ts.soakStartedAt = null;
      ts.readyAt = null;
      ts.totalTrapsPulled += 1;
      ts.totalCreaturesCaught += addedCount;
      ts.totalTrapValueEarned += totalValue;
    });

    // Emit TRAP_PULLED event
    eventBus.emit(GameEvent.TRAP_PULLED, result);

    console.log(`🪤 Trap pulled! ${addedCount} creatures caught (value: ${totalValue}g)`);

    // Save to localStorage
    saveManager.save();

    return result;
  }

  /**
   * Select or deselect trap bait.
   * @param baitId - Equipment ID of the trap bait, or null to deselect
   */
  selectTrapBait(baitId: string | null): void {
    if (baitId !== null) {
      const state = useGameStore.getState();

      // Validate bait exists in ownedEquipment with quantity > 0
      const ownedBait = state.playerState.ownedEquipment[baitId];
      if (!ownedBait || ownedBait.quantity <= 0) {
        console.warn('🪤 Cannot select trap bait - not owned or depleted:', baitId);
        return;
      }

      // Validate that the item is actually trap bait
      try {
        const definition = getEquipmentDefinition(baitId);
        if (!definition.isTrapBait) {
          console.warn('🪤 Cannot select as trap bait - not a trap bait item:', baitId);
          return;
        }
      } catch {
        console.warn('🪤 Cannot select trap bait - equipment not found:', baitId);
        return;
      }
    }

    useGameStore.setState((draft) => {
      draft.playerState.trapState.selectedTrapBait = baitId;
    });

    console.log(`🪤 Trap bait ${baitId ? 'selected: ' + baitId : 'deselected'}`);

    saveManager.save();
  }

  // === CREATURE GENERATION ===

  /**
   * Generate creatures for a trap pull based on equipped trap, bait, and zone pool.
   * Uses the same fish stat utilities as FishingEngine.
   */
  private generateCreatures(): { fish: FishInstance; definition: FishDefinition }[] {
    const state = useGameStore.getState();

    // Get trap equipment definition
    const equippedTrap = this.getEquippedTrap();
    if (!equippedTrap) {
      console.warn('🪤 No trap equipped during creature generation');
      return [];
    }

    // Get yield range from trap definition
    const yieldMin = equippedTrap.trapYieldMin ?? 3;
    const yieldMax = equippedTrap.trapYieldMax ?? 5;

    // Calculate base yield
    let yield_ = randomInt(yieldMin, yieldMax);

    // If bait was used, add bonus creatures
    const trapState = state.playerState.trapState;
    const baitUsed = trapState.selectedTrapBait;
    let baitQualityBonus = 0;

    if (baitUsed) {
      const bonusCreatures = randomInt(
        BALANCE_CONFIG.TRAP_BAIT_YIELD_BONUS_MIN,
        BALANCE_CONFIG.TRAP_BAIT_YIELD_BONUS_MAX
      );
      yield_ += bonusCreatures;

      // Accumulate quality bonus from bait
      try {
        const baitDef = getEquipmentDefinition(baitUsed);
        baitQualityBonus += (baitDef.stats.qualityBonus ?? 0);
      } catch {
        // Bait definition not found, ignore bonus
      }

      // Add the base bait quality bonus from balance config
      baitQualityBonus += BALANCE_CONFIG.TRAP_BAIT_QUALITY_BONUS;
    }

    // Cap yield at remaining inventory capacity
    const inventory = state.playerState.inventory;
    const remainingCapacity = inventory.capacity - inventory.currentCount;
    yield_ = Math.min(yield_, remainingCapacity);

    if (yield_ <= 0) {
      return [];
    }

    // Get current zone's trapPool
    const zone = getZoneDefinition(state.playerState.currentZone);
    const trapPool = zone.trapPool;

    if (!trapPool || trapPool.length === 0) {
      console.warn('🪤 No trap pool defined for zone:', zone.id);
      return [];
    }

    // Quality bonus from trap equipment stats
    const trapQualityBonus = equippedTrap.stats.qualityBonus ?? 0;
    const totalQualityBonus = trapQualityBonus + baitQualityBonus;

    // Generate each creature
    const creatures: { fish: FishInstance; definition: FishDefinition }[] = [];

    for (let i = 0; i < yield_; i++) {
      // Roll random species from trapPool (weighted by RarityWeights)
      const speciesId = this.rollTrapSpecies(trapPool);
      const definition = getFishDefinition(speciesId);

      // Generate stats using existing fish stat utilities
      const isWild = rollIsWild(definition.canBeHatchery);

      let length = rollFishLength(definition.lengthRange[0], definition.lengthRange[1]);
      let weight = rollFishWeight(length, definition.lengthRange, definition.weightRange);

      // Apply wild bonuses (5% boost for wild fish of hatchery-eligible species)
      if (isWild && definition.canBeHatchery) {
        length = applyWildLengthBonus(length, isWild);
        weight = applyWildWeightBonus(weight, isWild);
      }

      const health = rollFishHealth(isWild);

      // Calculate quality based on stats
      const quality = calculateFishQuality(
        length,
        weight,
        health,
        definition.lengthRange,
        definition.weightRange,
        definition.rarity,
        totalQualityBonus
      );

      // Create FishInstance
      const fish: FishInstance = {
        definitionId: speciesId,
        quality,
        caughtAt: Date.now(),
        length,
        weight,
        health,
        isWild,
      };

      creatures.push({ fish, definition });
    }

    return creatures;
  }

  /**
   * Roll a random species from the trap pool using rarity-weighted selection.
   * Same pattern as FishingEngine.rollFishSpecies.
   */
  private rollTrapSpecies(trapPool: string[]): string {
    const fishWithWeights = trapPool.map((fishId) => {
      const definition = getFishDefinition(fishId);
      return {
        id: fishId,
        weight: RarityWeights[definition.rarity] ?? 0.1,
      };
    });

    return weightedRandom(fishWithWeights);
  }

  // === TICK SYSTEM ===

  /**
   * Initialize the trap manager.
   * Starts a 1-second tick interval for soak/cooldown transitions.
   * On load, checks if a trap was soaking and restores state.
   */
  initialize(): void {
    // Clear any existing interval
    this.cleanup();

    // Start 1-second tick interval
    this.tickInterval = window.setInterval(() => {
      this.tick();
    }, 1000);

    // On load, check if trap was soaking and handle offline time
    const state = useGameStore.getState();
    const trapState = state.playerState.trapState;

    if (trapState.status === TrapStatus.SOAKING && trapState.readyAt) {
      if (Date.now() >= trapState.readyAt) {
        // Soak completed while offline — transition to READY
        useGameStore.setState((draft) => {
          draft.playerState.trapState.status = TrapStatus.READY;
        });
        console.log('🪤 Trap soak completed while offline — ready to pull!');
        eventBus.emit(GameEvent.TRAP_READY, {
          trapId: this.getEquippedTrap()?.id ?? 'unknown',
          soakDurationMs: trapState.soakDurationMs,
        });
      }
    } else if (trapState.status === TrapStatus.COOLDOWN && trapState.cooldownEndsAt) {
      if (Date.now() >= trapState.cooldownEndsAt) {
        // Cooldown expired while offline — transition to EMPTY
        useGameStore.setState((draft) => {
          const ts = draft.playerState.trapState;
          ts.status = TrapStatus.EMPTY;
          ts.cooldownEndsAt = null;
        });
        console.log('🪤 Trap cooldown expired while offline — ready to set!');
      }
    }

    console.log('🪤 TrapManager initialized');
  }

  /**
   * Tick handler — called every 1 second.
   * Handles automatic state transitions.
   */
  private tick(): void {
    const state = useGameStore.getState();
    const trapState = state.playerState.trapState;
    const now = Date.now();

    if (trapState.status === TrapStatus.SOAKING && trapState.readyAt) {
      if (now >= trapState.readyAt) {
        // Soak complete — transition to READY
        useGameStore.setState((draft) => {
          draft.playerState.trapState.status = TrapStatus.READY;
          draft.playerState.trapState.soakStartedAt = null;
        });

        console.log('🪤 Trap soak complete — ready to pull!');

        eventBus.emit(GameEvent.TRAP_READY, {
          trapId: this.getEquippedTrap()?.id ?? 'unknown',
          soakDurationMs: trapState.soakDurationMs,
        });
      }
    } else if (trapState.status === TrapStatus.COOLDOWN && trapState.cooldownEndsAt) {
      if (now >= trapState.cooldownEndsAt) {
        // Cooldown expired — transition to EMPTY
        useGameStore.setState((draft) => {
          const ts = draft.playerState.trapState;
          ts.status = TrapStatus.EMPTY;
          ts.cooldownEndsAt = null;
        });

        console.log('🪤 Trap cooldown complete — ready to set!');
      }
    }
  }

  /**
   * Clean up the tick interval.
   */
  cleanup(): void {
    if (this.tickInterval !== null) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
  }

  // === HELPERS ===

  /**
   * Check if the player can set a trap right now.
   * Validates: has trap equipped, status is EMPTY, inventory has room.
   */
  canSetTrap(): boolean {
    const state = useGameStore.getState();
    const trapState = state.playerState.trapState;

    // Status must be EMPTY
    if (trapState.status !== TrapStatus.EMPTY) {
      return false;
    }

    // Must have a trap equipped (not trap bait)
    const equippedTrap = this.getEquippedTrap();
    if (!equippedTrap || equippedTrap.isTrapBait) {
      return false;
    }

    // Must have inventory room
    const inventory = state.playerState.inventory;
    if (inventory.currentCount >= inventory.capacity) {
      return false;
    }

    return true;
  }

  /**
   * Get the currently equipped trap definition.
   * @returns EquipmentDefinition or null if no trap equipped
   */
  getEquippedTrap(): EquipmentDefinition | null {
    const state = useGameStore.getState();
    const trapId = state.playerState.equippedGear[EquipmentSlot.TRAP];

    if (!trapId) {
      return null;
    }

    try {
      const definition = getEquipmentDefinition(trapId);
      return definition;
    } catch {
      return null;
    }
  }

  /**
   * Get the time remaining for the current soak or cooldown in milliseconds.
   * Returns 0 if no timer is active.
   */
  getTimeRemaining(): number {
    const state = useGameStore.getState();
    const trapState = state.playerState.trapState;
    const now = Date.now();

    if (trapState.status === TrapStatus.SOAKING && trapState.readyAt) {
      return Math.max(0, trapState.readyAt - now);
    }

    if (trapState.status === TrapStatus.COOLDOWN && trapState.cooldownEndsAt) {
      return Math.max(0, trapState.cooldownEndsAt - now);
    }

    return 0;
  }
}

// Singleton instance
export const trapManager = new TrapManager();
